<template>
  <div>
    <PageHeaderBanner>
      <template #title>SDFile一覧</template>
    </PageHeaderBanner>
    <q-form autofocus greedy @submit.prevent="searchSdfList" @reset="resetForm">
      <div class="cmd00700-conditions-form">
        <q-card style="background: var(--q-lightGray)" flat bordered>
          <q-card-section class="q-gutter-y-md">
            <div :class="['row q-gutter-x-xs', !!word && 'cmd00700-disable']">
              <div class="col">
                <label>サプライヤ</label>
                <SupplierSelects
                  :supplier="supplier"
                  :disabled="!!word"
                  @select-supplier="supplier = $event"
                />
              </div>
              <div class="col">
                <label>カテゴリー</label>
                <CategorySelects
                  :category="category"
                  :disabled="!!word"
                  @select-category="category = $event"
                />
              </div>
            </div>
            <div :class="['row', (!!category || !!supplier) && 'cmd00700-disable']">
              <div class="col">
                <label>ワード検索</label>
                <q-input
                  v-model="word"
                  outlined
                  dense
                  style="background: var(--q-white)"
                  class="input-area"
                  :disable="!!category || !!supplier"
                  maxlength="200"
                  @keydown.enter.prevent
                  @clear="word = ''"
                />
                <div class="cmd00700-word-form q-mt-xs flex">
                  <p class="q-mb-none">SDFile名、備考を指定できます。<br /></p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col q-gutter-x-xs flex justify-end">
                <q-btn
                  type="submit"
                  color="primary"
                  style="width: 150px"
                  label="検索"
                  icon="mdi-magnify"
                />
                <q-btn
                  type="reset"
                  outline
                  style="
                    width: 150px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  icon="mdi-eraser"
                  label="クリア"
                />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </q-form>
    <div class="cmd00700-result-table">
      <div
        :class="
          'chem-table-area btn-exist table-top-button ' + (data.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-table"
          :rows="data"
          :columns="columns"
          row-key="index"
          flat
          dense
          :visible-columns="visibleColumns"
          separator="cell"
          :no-data-label="tableMessage"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <template #top>
            <div class="add-btn-area flex items-center justify-between filter-select">
              <q-select
                v-model="visibleColumns"
                multiple
                outlined
                dense
                options-dense
                display-value="カラム表示設定"
                emit-value
                map-options
                :options="visibleColumnOptions"
                option-value="name"
                color="primary"
                class="column-disp"
              />
              <div>
                <q-btn
                  color="primary"
                  :class="
                    'export-btn excel-exportbtn ' +
                    (data.length === 0 ? 'cmd00700-disabled-btn' : '')
                  "
                  :outline="data.length !== 0 ? true : false"
                  icon="mdi-download"
                  no-caps
                  :disable="data.length === 0"
                  @click="displayOutExcelDialog"
                  >Excel出力</q-btn
                >
                <q-tooltip
                  v-if="data.length === 0"
                  anchor="top middle"
                  self="bottom middle"
                  :offset="[15, 15]"
                >
                  <strong>検索結果が存在しません</strong>
                </q-tooltip>
              </div>
            </div>
          </template>

          <!-- 編集、削除Header -->
          <template #header-cell-editDelete>
            <q-th class="table-btn-area delete-btn-exists"></q-th>
          </template>
          <!-- 編集、削除Body -->
          <template #body-cell-editDelete="cell">
            <q-td class="table-btn-area delete-btn-exists">
              <div class="flex">
                <q-btn
                  icon="mdi-pencil"
                  label="編集"
                  color="primary"
                  @click="editData(cell.row.catalog_id)"
                />
                <q-btn
                  icon="mdi-close"
                  label="削除"
                  class="deletebtn"
                  outline
                  color="primary"
                  style="color: var(--q-delete)"
                  @click="displayDeleteSdfDialog(cell.row.catalog_id)"
                />
              </div>
            </q-td>
          </template>
        </q-table>
      </div>
    </div>
    <!-- 削除確認ダイアログ -->
    <AlertDialog
      :display="isDeleteDialog"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isDeleteDialog = false"
      @confirm="deleteSdf"
      @close="isDeleteDialog = false"
    />
    <!-- Excel出力確認ダイアログ -->
    <AlertDialog
      :display="isOutExcelDialog"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isOutExcelDialog = false"
      @confirm="downloadExcel()"
      @close="isOutExcelDialog = false"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { Loading } from 'quasar';
import { useRouter } from 'vue-router';
import saveAs from 'file-saver';
import axios from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import base64toBlob from '../../common/utils/fileUtils';
import commaFormat from '../../common/utils/numberUtils';
import CategorySelects from '../../components/selects/CategorySelects.vue';
import SupplierSelects from '../../components/selects/SupplierSelects.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';

// カラム表示機能
interface VisibleColumn {
  name: string;
  label: string;
}

// テーブルデータ
interface Rows {
  catalog_id: number;
  sup_category: string;
  supplier_code: string;
  supplier_name: string;
  sup_sdfile_name: string;
  item_num: number;
  poison_num: string;
  ban_num: string;
  special_num: string;
  update_timestamp: Date;
  memo: string;
}

const router = useRouter();

// 検索条件：サプライヤ
const supplier = ref('');
// 検索条件：カテゴリー
const category = ref('');
// 検索条件：ワード検索
const word = ref('');
// カタログID
const catalogId = ref('');

// 保持用検索条件：サプライヤ
const saveSupplier = ref('');
// 保持用検索条件：カテゴリー
const saveCategory = ref('');
// 保持用検索条件：ワード検索
const saveWord = ref('');

// 表示カラム
const visibleColumns = ref<Array<string>>([]);
// カラム表示設定の選択肢
const visibleColumnOptions = ref<Array<VisibleColumn>>([]);
// テーブルメッセージ
const tableMessage = ref('検索してください。');

// Dialogタイトル
const dialogTitle = ref('');
// Dialog表示レベル
const dialogLevel = ref('');
// Dialog本文
const dialogText = ref(['']);
// 確認Dialogフラグ
const isConfFlg = ref(false);
// 削除確認Dialog表示フラグ
const isDeleteDialog = ref(false);
// Excel出力確認Dialog表示フラグ
const isOutExcelDialog = ref(false);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: Array<any> = [
  {
    name: 'editDelete',
    field: (row: Rows) => row.catalog_id,
    required: true,
  },
  {
    name: 'catalog_id',
    label: ' カタログID',
    align: 'left',
    field: (row: Rows) => row.catalog_id,
    sortable: true,
    classes: 'text-right',
  },
  {
    name: 'sup_category',
    label: 'カテゴリー',
    align: 'left',
    field: (row: Rows) => row.sup_category,
    sortable: true,
  },
  {
    name: 'supplier_code',
    label: 'サプライヤコード',
    align: 'left',
    field: (row: Rows) => row.supplier_code,
    sortable: true,
  },
  {
    name: 'supplier_name',
    label: 'サプライヤ名',
    align: 'left',
    field: (row: Rows) => row.supplier_name,
    sortable: true,
  },
  {
    name: 'sup_sdfile_name',
    label: 'SDFile名',
    align: 'left',
    field: (row: Rows) => row.sup_sdfile_name,
    sortable: true,
  },
  {
    name: 'item_num',
    label: '品目数',
    align: 'left',
    field: (row: Rows) => (row.item_num ? commaFormat(row.item_num) : 0),
    classes: 'text-right',
    sortable: true,
    style: 'min-width: 120px',
  },
  {
    name: 'poison_num',
    label: '毒劇品目数',
    align: 'left',
    field: (row: Rows) => (row.poison_num ? commaFormat(Number(row.poison_num)) : '未実施'),
    classes: 'text-right',
    sortable: true,
    style: 'min-width: 120px',
  },
  {
    name: 'ban_num',
    label: '輸入禁止品目数',
    align: 'left',
    field: (row: Rows) => (row.ban_num ? commaFormat(Number(row.ban_num)) : '未実施'),
    classes: 'text-right',
    sortable: true,
    style: 'min-width: 120px',
  },
  {
    name: 'special_num',
    label: '法規制チェックエラー品目数',
    align: 'left',
    field: (row: Rows) => (row.special_num ? commaFormat(Number(row.special_num)) : '未実施'),
    classes: 'text-right',
    sortable: true,
    style: 'min-width: 120px',
  },
  {
    name: 'update_timestamp',
    label: '更新日時',
    align: 'left',
    field: (row: Rows) => row.update_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
  {
    name: 'memo',
    label: '備考',
    align: 'left',
    field: (row: Rows) => row.memo,
    sortable: true,
  },
];

// 検索結果
const data = ref<Rows[]>([]);

/**
 * SDFile削除確認ダイアログ表示
 * @param {string} catalog カタログID
 */
const displayDeleteSdfDialog = (catalog: string) => {
  catalogId.value = catalog;
  dialogTitle.value = '削除確認';
  dialogLevel.value = 'warning';
  dialogText.value = [
    `カタログID:${catalog}に紐づく商品データをすべて削除します。`,
    '本当によろしいですか？',
  ];
  isConfFlg.value = false;
  isDeleteDialog.value = true;
};

/**
 * Excel出力確認ダイアログ表示
 */
const displayOutExcelDialog = () => {
  dialogTitle.value = 'Excel出力確認';
  dialogLevel.value = 'information';
  dialogText.value = ['検索結果のExcelファイルを出力します。', 'よろしいですか？'];
  isConfFlg.value = false;
  isOutExcelDialog.value = true;
};

/**
 * フォーム初期化
 */
const resetForm = () => {
  category.value = '';
  supplier.value = '';
  word.value = '';
};

/**
 * SDFileリスト検索
 */
const searchSdfList = async () => {
  // Loading Start
  Loading.show();
  // 検索用データ作成
  const formData = new FormData();
  formData.append('supplier_code', supplier.value);
  formData.append('category_id', category.value);
  formData.append('word', word.value);

  // 保持用のデータを設定
  saveSupplier.value = supplier.value;
  saveCategory.value = category.value;
  saveWord.value = word.value;

  // SDFileリスト取得
  await axios
    .post('/cmd007001/search-sdf-list', formData)
    .then((res) => {
      const { common, sdFileList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          data.value = sdFileList;
          tableMessage.value = 'データがありません。';
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * Excel出力
 */
const downloadExcel = async () => {
  // Loading Start
  Loading.show();
  // 検索用データ作成
  const formData = new FormData();
  formData.append('supplier_code', saveSupplier.value);
  formData.append('category_id', saveCategory.value);
  formData.append('word', saveWord.value);

  // SDFileリスト取得
  await axios
    .post('/cmd007001/download-excel', formData)
    .then((res) => {
      const { common, body, file_name } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS: {
          const blob = new Blob(
            [
              base64toBlob(
                body,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              ),
            ],
            {}
          );
          saveAs(blob, file_name);
          // ダイアログ表示
          dialogTitle.value = 'Excel出力完了';
          dialogLevel.value = 'information';
          dialogText.value = ['検索結果のExcelファイルを出力しました。'];
          isConfFlg.value = true;
          isOutExcelDialog.value = true;
          break;
        }
        // エラーの場合
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * SDFile削除
 */
const deleteSdf = async () => {
  // Loading Start
  Loading.show();
  // 検索用データ作成
  const formData = new FormData();
  formData.append('catalog_id', catalogId.value);
  formData.append('supplier_code', saveSupplier.value);
  formData.append('category_id', saveCategory.value);
  formData.append('word', saveWord.value);

  // SDFile削除
  await axios
    .post('/cmd007001/delete-sdf', formData)
    .then((res) => {
      const { common, sdFileList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          data.value = sdFileList;
          dialogTitle.value = '削除完了';
          dialogLevel.value = 'information';
          dialogText.value = ['削除が完了しました。'];
          isConfFlg.value = true;
          isDeleteDialog.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.UPDATE_ERROR:
              dialogTitle.value = '削除エラー';
              dialogLevel.value = 'error';
              dialogText.value = [common.message];
              isConfFlg.value = true;
              isDeleteDialog.value = true;
              break;
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * SDFile変更画面に遷移
 * @param {number} editCatalogId カタログID
 */
const editData = (editCatalogId: number) => {
  router.push({ name: 'sdfileEdit', params: { catalogId: editCatalogId } });
};

/**
 * 初期表示処理
 */
// カラム表示設定
for (let i = 0; i < columns.length; i += 1) {
  visibleColumns.value.push(columns[i].name);
  // 編集削除ボタンのカラムは除外
  if (columns[i].name !== 'editDelete') {
    visibleColumnOptions.value.push({
      name: columns[i].name,
      label: columns[i].label,
    });
  }
}
</script>
<style scoped>
.cmd00700-conditions-form {
  width: 70%;
  margin: 0 auto 30px;
}
.cmd00700-result-table {
  width: 90%;
  margin: 0 auto;
}
.cmd00700-disable {
  opacity: 0.3;
}
.cmd00700-word-form {
  font-size: 0.8rem;
  width: 100%;
  margin-bottom: 5px;
  color: var(--q-plainBlack);
  display: flex;
}
</style>
<style>
.cmd00700-result-table .table-top-button.chem-table-area .q-table__container {
  margin-top: 70px !important;
}

.cmd00700-result-table .chem-table-area .q-table__bottom.row {
  right: 150px;
}

.cmd00700-result-table .q-table__top {
  width: 100%;
}

.cmd00700-result-table .cmd00700-disabled-btn {
  background-color: var(--q-opacityDisable) !important;
  color: #fff !important;
}
</style>
